import { useId } from 'react';
import type { PayoutTemplateRow } from '@/types/reveal';

const CHART_VIEWBOX_WIDTH = 305;
const CHART_VIEWBOX_HEIGHT = 130;

/** mt-1 below label + 10px dot; line runs from dot bottom to meter bottom (chart bottom). */
const PRIZE_CONNECTOR_TOP_GAP_PX = 4;
const PRIZE_CONNECTOR_DOT_DIAMETER_PX = 10;
const PRIZE_CONNECTOR_LINE_TOP_OFFSET_PX =
  PRIZE_CONNECTOR_TOP_GAP_PX + PRIZE_CONNECTOR_DOT_DIAMETER_PX;

// Cubic bezier control points extracted from the SVG meter path segment:
// "C117.596 117.962 220.857 73.4123 298.134 1.10443" starting from (4, 118.987)
const BZ = {
  p0: { x: 4, y: 118.987 },
  p1: { x: 117.596, y: 117.962 },
  p2: { x: 220.857, y: 73.4123 },
  p3: { x: 298.134, y: 1.10443 },
} as const;

function evalBezier(t: number, p0: number, p1: number, p2: number, p3: number): number {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
}

// Binary search for bezier t given a target x (valid because control point x-coords are monotone)
function solveBezierT(targetX: number): number {
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (evalBezier(mid, BZ.p0.x, BZ.p1.x, BZ.p2.x, BZ.p3.x) < targetX) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return (lo + hi) / 2;
}

function getBottomForX(xPercent: number): number {
  const svgX = (Math.max(0, Math.min(100, xPercent)) / 100) * CHART_VIEWBOX_WIDTH;
  const clampedSvgX = Math.max(BZ.p0.x, Math.min(BZ.p3.x, svgX));
  const t = solveBezierT(clampedSvgX);
  const svgY = evalBezier(t, BZ.p0.y, BZ.p1.y, BZ.p2.y, BZ.p3.y);
  // curveBottom: pixels from the container bottom (SVG is CHART_VIEWBOX_HEIGHT px tall, at bottom-0)
  const curveBottom = CHART_VIEWBOX_HEIGHT - svgY;
  // Scale + margin positions the label above the curve with proportional clearance
  // Constants derived from fitting the original reference data at the endpoints
  return Math.round(curveBottom * 1.5 + 6);
}

function formatWeeklyRange(): string {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', opts)} - ${end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

function formatBonusBetDisplay(amount: number): string {
  if (amount >= 1000) {
    const inThousands = amount / 1000;
    const compact = Number.isInteger(inThousands) ? inThousands.toFixed(0) : inThousands.toFixed(1);
    return `$${compact}k`;
  }
  const base = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
  return `$${base}`;
}

type PrizeTableSectionProps = {
  currentHits?: number;
  hasEntered?: boolean;
  dateRange?: string;
  payoutTemplateRows?: PayoutTemplateRow[];
};

export default function PrizeTableSection({
  currentHits = 0,
  hasEntered = false,
  dateRange,
  payoutTemplateRows,
}: PrizeTableSectionProps) {
  const displayDate = dateRange ?? formatWeeklyRange();
  const clipId = useId().replace(/:/g, '');

  if (!payoutTemplateRows || payoutTemplateRows.length === 0) return null;

  const rows = payoutTemplateRows;
  const n = rows.length;
  const hasRankZero = rows[0]?.rank === 0;
  const clampedHits = Math.min(currentHits, n);
  const activeIndex = hasRankZero
    ? hasEntered
      ? Math.min(currentHits, n - 1)
      : -1
    : clampedHits <= 0
      ? -1
      : Math.min(clampedHits - 1, n - 1);

  // Fill should stop at the active marker line, except the last index fills to the chart end.
  const progressRatio = activeIndex < 0 ? 0 : activeIndex === n - 1 ? 1 : (activeIndex + 0.5) / n;
  const progressWidth = CHART_VIEWBOX_WIDTH * progressRatio;

  type DerivedPoint = {
    label: string;
    mainValuePrefix: string;
    mainValue: string;
    subLabel: string;
    xPercent: number;
    bottom: number;
    isBonusBet: boolean;
  };

  const points: DerivedPoint[] = rows.map((row, i) => {
    const xPercent = ((i + 0.5) / n) * 100;
    const label = String(row.rank);
    const bottom = getBottomForX(xPercent);
    const bonusBetAmount = parseFloat(row.bonusBet || '0');
    const isBonusBet = bonusBetAmount > 0;
    const mainValuePrefix = isBonusBet ? 'Share of' : '';
    const mainValue = isBonusBet
      ? formatBonusBetDisplay(bonusBetAmount)
      : `${parseFloat(row.profitBoostToken || '0')}%`;
    const subLabel = isBonusBet ? 'bonus\nbet' : 'Profit\nBoost';
    return { label, mainValuePrefix, mainValue, subLabel, xPercent, bottom, isBonusBet };
  });

  return (
    <section className="relative h-[372px] w-full overflow-hidden rounded-[9px]">
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, #121212 0%, #242424 50%, #121212 100%)',
        }}
      ></div>
      <div
        className="absolute inset-0"
        aria-hidden
        style={{
          background:
            'linear-gradient(90deg, rgba(18,18,18,0.95) 0%, rgba(36,36,36,0.65) 50%, rgba(18,18,18,0.95) 100%)',
        }}
      ></div>
      <div
        className="absolute inset-0"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse at 50% 8%, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0) 55%)',
        }}
      ></div>

      <div className="relative z-10 h-full px-4 pb-0 pt-3">
        <div className="relative z-0 mt-1 h-[300px] w-full">
          {/* Number row: each value in its own column */}
          <div className="absolute left-0 right-0 top-[8px] flex">
            {points.map((point, idx) => (
              <div key={point.label} className="flex flex-1 justify-center">
                <span
                  className={idx === activeIndex ? 'text-[#53d337]' : 'text-white'}
                  style={{
                    fontFamily: "'Druk Text Wide', 'Arial Black', sans-serif",
                    fontWeight: 700,
                    fontSize: '18px',
                    lineHeight: 1,
                  }}
                >
                  {point.label}
                </span>
              </div>
            ))}
          </div>

          {/* Vertical separators: n lanes = n+1 lines */}
          {Array.from({ length: n + 1 }, (_, i) => (
            <div
              key={`lane-${i}`}
              className="absolute w-px"
              style={{
                left: `${(i / n) * 100}%`,
                top: '32px',
                height: '190px',
                background:
                  'linear-gradient(180deg, rgba(169,173,181,0.75) 0%, rgba(169,173,181,0.4) 55%, rgba(169,173,181,0) 100%)',
              }}
              aria-hidden
            />
          ))}

          <svg
            viewBox={`0 0 ${CHART_VIEWBOX_WIDTH} 130`}
            className="absolute bottom-0 left-0 z-10 h-[130px] w-full"
            preserveAspectRatio="none"
            aria-hidden
          >
            <defs>
              <linearGradient
                id={`weekly-meter-base-${clipId}`}
                x1="0"
                y1="58.6251"
                x2={CHART_VIEWBOX_WIDTH}
                y2="58.6251"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#151515" />
                <stop offset="1" stopColor="#404040" />
              </linearGradient>
              <linearGradient
                id={`weekly-meter-fill-${clipId}`}
                x1="0"
                y1="58.6251"
                x2={CHART_VIEWBOX_WIDTH}
                y2="58.6251"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#F7760D" />
                <stop offset="1" stopColor="#61B510" />
              </linearGradient>
              <clipPath id={`weekly-meter-progress-clip-${clipId}`}>
                <rect x="0" y="0" width={progressWidth} height="130" />
              </clipPath>
            </defs>
            <path
              d="M305 125.699C305 127.908 303.209 129.699 301 129.699H4C1.79086 129.699 0 127.908 0 125.699V123.005C0 120.796 1.79824 119.007 4.00729 118.987C117.596 117.962 220.857 73.4123 298.134 1.10443C300.72 -1.31497 305 0.498087 305 4.03916V125.699Z"
              fill={`url(#weekly-meter-base-${clipId})`}
            />
            <path
              d="M305 125.699C305 127.908 303.209 129.699 301 129.699H4C1.79086 129.699 0 127.908 0 125.699V123.005C0 120.796 1.79824 119.007 4.00729 118.987C117.596 117.962 220.857 73.4123 298.134 1.10443C300.72 -1.31497 305 0.498087 305 4.03916V125.699Z"
              fill={`url(#weekly-meter-fill-${clipId})`}
              clipPath={`url(#weekly-meter-progress-clip-${clipId})`}
            />
            <path
              d="M305 125.699C305 127.908 303.209 129.699 301 129.699H4C1.79086 129.699 0 127.908 0 125.699V123.005C0 120.796 1.79824 119.007 4.00729 118.987C117.596 117.962 220.857 73.4123 298.134 1.10443C300.72 -1.31497 305 0.498087 305 4.03916V125.699Z"
              fill="none"
              stroke="#51545C"
              strokeWidth="1"
            />
          </svg>

          {/* Reward labels rising with the meter, one per lane, no overlaps */}
          {points.map((point, idx) => {
            if (idx === activeIndex) return null;
            return (
              <div
                key={point.label}
                className="absolute min-w-[44px] -translate-x-1/2"
                style={{ left: `${point.xPercent}%`, bottom: `${point.bottom}px` }}
              >
                <div className="flex flex-col items-center gap-1">
                  {point.isBonusBet ? (
                    <div className="flex flex-col items-center">
                      <span
                        className="text-center text-[#9da0a7]"
                        style={{
                          fontFamily: "'Saira Semi Condensed', sans-serif",
                          fontWeight: 400,
                          fontSize: '9px',
                          lineHeight: 1.35,
                        }}
                      >
                        {point.mainValuePrefix}
                      </span>
                      <span
                        className="text-center text-[#9da0a7]"
                        style={{
                          fontFamily: "'Saira Semi Condensed', sans-serif",
                          fontWeight: 700,
                          fontSize: '16px',
                          lineHeight: 1.2,
                        }}
                      >
                        {point.mainValue}
                      </span>
                    </div>
                  ) : (
                    <span
                      className="text-center text-[#9da0a7]"
                      style={{
                        fontFamily: "'Saira Semi Condensed', sans-serif",
                        fontWeight: 700,
                        fontSize: '16px',
                        lineHeight: 1.2,
                      }}
                    >
                      {point.mainValue}
                    </span>
                  )}
                  <span
                    className="text-center text-[#8a8a8a]"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '9px',
                      lineHeight: 1.35,
                    }}
                  >
                    {point.subLabel.split('\n').map((line, i) => (
                      <span key={i}>
                        {i > 0 && <br />}
                        {line}
                      </span>
                    ))}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Active boost indicator: shown only when user has at least 1 hit */}
          {activeIndex >= 0 && (
            <div
              className="absolute z-30 -translate-x-1/2"
              style={{
                left: `${points[activeIndex].xPercent}%`,
                bottom: `${points[activeIndex].bottom}px`,
              }}
            >
              {points[activeIndex].isBonusBet ? (
                <div className="flex flex-col items-center gap-1">
                  <div className="flex flex-col items-center">
                    <span
                      className="text-center text-white"
                      style={{
                        fontFamily: "'Saira Semi Condensed', sans-serif",
                        fontWeight: 400,
                        fontSize: '9px',
                        lineHeight: 1.35,
                      }}
                    >
                      {points[activeIndex].mainValuePrefix}
                    </span>
                    <span
                      className="text-center text-[#53d337]"
                      style={{
                        fontFamily: "'Saira Semi Condensed', sans-serif",
                        fontWeight: 700,
                        fontSize: '16px',
                        lineHeight: 1.2,
                      }}
                    >
                      {points[activeIndex].mainValue}
                    </span>
                  </div>
                  <span
                    className="text-center text-white"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '9px',
                      lineHeight: 1.35,
                    }}
                  >
                    {points[activeIndex].subLabel.split('\n').map((line, i) => (
                      <span key={i}>
                        {i > 0 && <br />}
                        {line}
                      </span>
                    ))}
                  </span>
                </div>
              ) : (
                <div className="relative flex flex-col items-center">
                  <img
                    src="/shapes/correct2.png"
                    alt=""
                    aria-hidden
                    className="mb-1 h-[22px] w-[22px] object-contain"
                  />
                  <span
                    className="leading-none text-[#53d337]"
                    style={{
                      fontFamily: "'Saira Semi Condensed', sans-serif",
                      fontWeight: 800,
                      fontSize: '24px',
                    }}
                  >
                    {points[activeIndex].mainValue}
                  </span>
                  <span
                    className="mt-[3px] text-center text-white"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '10px',
                      lineHeight: 1.25,
                    }}
                  >
                    {points[activeIndex].subLabel.split('\n').map((line, i) => (
                      <span key={i}>
                        {i > 0 && <br />}
                        {line}
                      </span>
                    ))}
                  </span>
                  {(() => {
                    const anchorBottomPx = points[activeIndex].bottom;
                    // Label stack bottom sits at anchorBottomPx from chart bottom; connector starts
                    // (gap + dot) below that, so line must span the rest to meet the meter baseline.
                    const connectorLinePx = Math.max(
                      anchorBottomPx - PRIZE_CONNECTOR_LINE_TOP_OFFSET_PX,
                      0
                    );
                    const dotR = PRIZE_CONNECTOR_DOT_DIAMETER_PX / 2;
                    const lineW = 3;
                    const svgH = dotR * 2 + connectorLinePx;
                    return (
                      <div className="absolute top-[100%] z-40 mt-1 flex justify-center">
                        <svg
                          width={dotR * 2}
                          height={svgH}
                          viewBox={`0 0 ${dotR * 2} ${svgH}`}
                          className="block shrink-0 overflow-visible"
                          aria-hidden
                        >
                          <circle cx={dotR} cy={dotR} r={dotR} fill="#FFFFFF" />
                          <rect
                            x={dotR - lineW / 2}
                            y={dotR * 2}
                            width={lineW}
                            height={connectorLinePx}
                            fill="#FFFFFF"
                          />
                        </svg>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        <p
          className="mt-0 text-center text-[#8a8a8a]"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: '13px',
          }}
        >
          {displayDate}
        </p>
      </div>
    </section>
  );
}
